import { create } from 'zustand';
import _ from 'lodash';
import WorkRequestOrderService from '~/modules/services/work-request-order-service';
import WorkRequestService from '~/modules/services/work-request-service';
import { v4 as uuidv4 } from 'uuid';
import { isDialogueOrder, isGfxOrder } from '~/utils/orders';
import IdService from '~/modules/services/id-service';
import TranslationService from '~/modules/services/translation-service';
import UploadService from '~/modules/services/upload-service';

const useWorkRequestQueueStore = create((set) => ({
	currentOrderAsset: {},
	workRequestQueue: [],
	isPlacingOrder: null,
	fileUploadProgress: null,
	loadError: null,
	retryOrCancel: null,
	setRetryOrCancel: (retryOrCancel) => set({ retryOrCancel }),
	updateFileUploadProgress: (fileId, progress) => set((state) => {
		return {
			isPlacingOrder: {
				isSubmittingWorkRequest: false,
				isUpdatingTranslations: false,
				isUploadingFiles: true
			},
			fileUploadProgress: {
				...state.fileUploadProgress,
				[fileId]: progress
			}
		};
	}),
	setFileUploadProgress: (fileUploadProgress) => set({
		isPlacingOrder: {
			isSubmittingWorkRequest: false,
			isUpdatingTranslations: false,
			isUploadingFiles: true
		},
		fileUploadProgress
	}),
	setIsPlacingOrder: (isPlacingOrder) => set({ isPlacingOrder }),
	addToQueue: (toAdd) => set((state) => {
		let workRequestQueue = state.workRequestQueue;
		if( _.isArray(toAdd) ) {
			// use union to avoid duplicates
			workRequestQueue = [...new Set([...state.workRequestQueue, ...toAdd])];
		}
		else if( state.workRequestQueue.indexOf(toAdd) === -1 ) {
			workRequestQueue = [...state.workRequestQueue, toAdd];
		}
		return { workRequestQueue };
	}),

	removeFromQueue: (toRemove) => set((state) => {
		let workRequestQueue;
		if( _.isArray(toRemove) ) {
			workRequestQueue = state.workRequestQueue.filter((id) => !toRemove.includes(id));
		}
		else {
			workRequestQueue = state.workRequestQueue.filter((id) => toRemove !== id);
		}

		return { workRequestQueue };
	}),

	clearQueue: () => set({
		currentOrderAsset: {},
		workRequestQueue: []
	}),

	isAssetInQueue: (assetId) => {
		if( !assetId ) {
			return false;
		}
		return _.get(useWorkRequestQueueStore.getState(), 'workRequestQueue', []).includes(assetId);
	},
	isOrderQueueEmpty: () => {
		return _.get(useWorkRequestQueueStore.getState(), 'workRequestQueue', []).length === 0;
	},
	setCurrentOrderAsset: (asset) => set({ currentOrderAsset: asset }),
	waitForUserAction: () => {
		return new Promise((resolve) => {
			const unsubscribe = useWorkRequestQueueStore.subscribe(
				(state) => state.retryOrCancel,
				(retryOrCancel) => {
					if( retryOrCancel ) {
						unsubscribe();
						resolve(retryOrCancel);
					}
				}
			);
		});
	},
	placeWorkRequestOrder: (order, assetType) => {
		const state = useWorkRequestQueueStore.getState();

		// Return the promise directly rather than a function that returns a promise
		return (async() => {
			try {
				if(!order) {
					throw new Error('Cannot place order. Order not found');
				}

				// Update state to show work request submission is in progress
				state.setIsPlacingOrder({
					isSubmittingWorkRequest: true,
					isUpdatingTranslations: false,
					isUploadingFiles: false,
					didFailWorkRequestOrder: false,
					didFailFileUpload: false,
					didFailTranslations: false
				});

				let needsTranslation = WorkRequestOrderService.doesOrderNeedTranslation(order);
				let transformedOrder = WorkRequestOrderService.toWorkRequest(order, state.currentOrderAsset, assetType);

				// If the asset has a workflow, delete the empty tags array from the transformed order
				// POST /work-requests failed if tags is an empty array
				if(state.currentOrderAsset.workflow?.id) {
					delete transformedOrder.tags;
				}

				// Create work request
				let workRequest = await WorkRequestService.create(transformedOrder);

				let tagFiles = order.tags.tags.map((tag, tagIndex) => {
					return tag.files.map((file, fileIndex) => {
						return {
							id: uuidv4(),
							tagIndex,
							fileIndex,
							tag,
							file,
							workRequestId: workRequest._id
						};
					});
				});
				tagFiles = _.flatten(tagFiles);

				// Generate upload requests for each file
				let files = order.additionalInfo.files.map((file, fileIndex) => {
					return {
						id: uuidv4(),
						fileIndex,
						file,
						workRequestId: workRequest._id
					};
				});

				if(tagFiles.length || files.length) {
					let allFiles = [...tagFiles, ...files];
					let fileUploadProgress = _.reduce(allFiles, (result, tagFile) => {
						result[tagFile.id] = {
							name: tagFile.file.name,
							progress: 0
						};
						return result;
					}, {});

					state.setFileUploadProgress(fileUploadProgress);

					// Repeat until all uploads are successful (retry enabled)
					while(tagFiles.length || files.length) {
						allFiles = [...tagFiles, ...files];
						let uploadTasks = [];

						for(let file of allFiles) {
							let task = state.uploadFile(file);
							uploadTasks.push(task);
						}

						let uploadResults = await Promise.all(uploadTasks);

						// Split upload results back into tag files and regular files
						let tagFileUploadResults = _.filter(
							uploadResults,
							({ uploadRequest }) => Object.prototype.hasOwnProperty.call(uploadRequest, 'tagIndex')
						);
						let fileUploadResults = _.reject(
							uploadResults,
							({ uploadRequest }) => Object.prototype.hasOwnProperty.call(uploadRequest, 'tagIndex')
						);

						// Update work request
						tagFiles = _.reduce(tagFileUploadResults, (result, tagFileUploadResult) => {
							// Requeue failed uploads
							if(_.isError(tagFileUploadResult.result)) {
								result.push(tagFileUploadResult.uploadRequest);
								return result;
							}

							// Update the work request tag file paths
							let { tagIndex, fileIndex } = tagFileUploadResult.uploadRequest;
							workRequest.tags[tagIndex].files[fileIndex] = tagFileUploadResult.result;
							return result;
						}, []);

						files = _.reduce(fileUploadResults, (result, fileUploadResult) => {
							// Requeue failed uploads
							if(_.isError(fileUploadResult.result)) {
								result.push(fileUploadResult.uploadRequest);
								return result;
							}

							// Update the work request file paths
							let { fileIndex } = fileUploadResult.uploadRequest;
							workRequest.files[0].files[fileIndex] = fileUploadResult.result;
							return result;
						}, []);

						const updatedWorkRequest = await WorkRequestService.update(workRequest._id, {
							files: workRequest.files,
							tags: workRequest.tags
						});
						workRequest = { ...workRequest, ...updatedWorkRequest };

						state.setIsPlacingOrder({ ...state.isPlacingOrder, isUploadingFiles: false });

						// If there were any upload failures, wait for user input (retry/cancel failed uploads)
						if(tagFiles.length || files.length) {
							state.setIsPlacingOrder({
								...state.isPlacingOrder,
								didFailFileUpload: true
							});

							const retryOrCancel = await state.waitForUserAction();

							if(retryOrCancel === 'cancel') {
								state.setIsPlacingOrder({
									...state.isPlacingOrder,
									isUploadingFiles: false,
									didFailFileUpload: false
								});
								tagFiles.length = 0;
								files.length = 0;
							}
							else {
								state.setIsPlacingOrder({
									...state.isPlacingOrder,
									isUploadingFiles: true,
									didFailFileUpload: false
								});
							}
							state.setRetryOrCancel(null);
						}
					}
				}

				state.setIsPlacingOrder({ ...state.isPlacingOrder, isUploadingFiles: false });

				if(needsTranslation) {
					state.setIsPlacingOrder({ ...state.isPlacingOrder, isUpdatingTranslations: true });

					// check if the order is a graphics order to update the workRequest.graphicsTranslation instead of workRequest.translation
					const graphicsTranslationId = isGfxOrder(workRequest)
						? workRequest?.graphicsTranslation?._id
						: false;
					let translationId = isDialogueOrder(workRequest)
						? workRequest?.translation?._id
						: false;

					let translationUpdate = {};
					let graphicsTranslationUpdate = {};
					let multiTranslationsUpdate = [];

					// Resolve a translator value (from the contact picker / order) into the shape
					// the API expects. A translator may be:
					//   - a plain string (new-user email typed into the contact picker)
					//   - a user object with an `id` (existing user)
					//   - a user object without an `id` but with an email (new-user object form)
					// For new users we send the email string so the API's NormalizeAndInvite filter
					// can invite them; for existing users we convert `id` -> `_id`.
					const resolveTranslator = (translator) => {
						if(!translator) {
							return null;
						}
						if(typeof translator === 'string') {
							return translator;
						}
						if(translator.id) {
							return IdService.convertFromId(translator);
						}
						// No id available — fall back to the email so a new user can be invited.
						return translator.email ?? translator.emailNormal ?? null;
					};

					// If translator is 3rd party subtitling vendor, does translation need approval?
					const isThirdPartyTranslator = order.localization.translationProviderType === 'email';

					// If the order is multiTranslation :
					// each multiTranslations translation file ID (multiTranslations[x].translation)
					// should be updated with the translationProvider (multiTranslations[x].translator._id)
					if(workRequest.multiTranslations?.length > 0) {
						workRequest.multiTranslations.forEach((multiTranslation) => {
							// Look up the original translator from the order (matched by language).
							// The translator on the workRequest is just a User id (or undefined for new
							// email users since the work request schema only stores user ids), so we
							// rely on the order to recover the full User object or email string.
							const orderMultiTranslation = order.localization?.multiTranslations?.find(
								(item) => item.language === multiTranslation.language
							);
							const translator = resolveTranslator(
								orderMultiTranslation?.translator ?? multiTranslation.translator
							);
							if(!translator) {
								return;
							}
							multiTranslationsUpdate.push({
								_id: multiTranslation.translation,
								translator,
								...(isThirdPartyTranslator
									? { approval: !!order.localization.needsApproval }
									: {})
							});
						});
					}

					// Current user is the default translator. Update that if a translationProvider is provided.
					if(order.localization.translationProvider) {
						const resolvedTranslator = resolveTranslator(order.localization.translationProvider);
						if(resolvedTranslator) {
							if(translationId) {
								translationUpdate.translator = resolvedTranslator;
							}
							if(graphicsTranslationId) {
								graphicsTranslationUpdate.translator = resolvedTranslator;
							}
						}
					}

					if(isThirdPartyTranslator) {
						if(translationId) {
							translationUpdate.approval = order.localization.needsApproval;
						}
						if(graphicsTranslationId) {
							graphicsTranslationUpdate.approval = order.localization.needsApproval;
						}
					}

					// If translations are provided via a file, start the file upload phase
					let needsFileUpload = order.localization.translationProviderType === 'upload';
					while(needsFileUpload) {
						if(translationId) {
							try {
								let uploadResult = await state.uploadTranslations(
									translationId,
									order.localization.translationFile
								);

								if(_.isError(uploadResult.result)) {
									throw uploadResult.result;
								}

								translationUpdate.file = uploadResult.result;
								translationUpdate.status = 'submitted';
								needsFileUpload = false;
							}
							catch{
								state.setIsPlacingOrder({
									...state.isPlacingOrder,
									isUpdatingTranslations: false,
									didFailTranslations: true
								});

								const retryOrCancel = await state.waitForUserAction();

								if(retryOrCancel === 'cancel') {
									state.setIsPlacingOrder({
										...state.isPlacingOrder,
										isUpdatingTranslations: false,
										didFailTranslations: false
									});
									needsFileUpload = false;
									delete translationUpdate.file;
									delete translationUpdate.status;
								}
								else {
									state.setIsPlacingOrder({
										...state.isPlacingOrder,
										isUpdatingTranslations: true,
										didFailTranslations: false
									});
								}
								state.setRetryOrCancel(null);
							}
						}

						if(graphicsTranslationId) {
							try {
								let uploadResult = await state.uploadTranslations(
									graphicsTranslationId,
									order.localization.translationFile
								);

								if(_.isError(uploadResult.result)) {
									throw uploadResult.result;
								}

								graphicsTranslationUpdate.file = uploadResult.result;
								graphicsTranslationUpdate.status = 'submitted';
								needsFileUpload = false;
							}
							catch{
								state.setIsPlacingOrder({
									...state.isPlacingOrder,
									isUpdatingTranslations: false,
									didFailTranslations: true
								});

								const retryOrCancel = await state.waitForUserAction();

								if(retryOrCancel === 'cancel') {
									needsFileUpload = false;
									delete graphicsTranslationUpdate.file;
									delete graphicsTranslationUpdate.status;
								}
								else {
									state.setIsPlacingOrder({
										...state.isPlacingOrder,
										isUpdatingTranslations: true,
										didFailTranslations: false
									});
								}
								state.setRetryOrCancel(null);
							}
						}
					}

					if(!_.isEmpty(translationUpdate) || !_.isEmpty(graphicsTranslationUpdate)) {
						if(translationId) {
							await TranslationService.updateTranslation(translationId, translationUpdate);
						}
						if(graphicsTranslationId) {
							await TranslationService.updateTranslation(
								graphicsTranslationId,
								graphicsTranslationUpdate
							);
						}
					}

					// For multiTranslations, update each translation with the translator ID
					// as the original translation files are created with the default translator
					if(multiTranslationsUpdate.length > 0) {
						await Promise.all(
							multiTranslationsUpdate.map((multiTranslation) => TranslationService.updateTranslation(
								multiTranslation._id,
								multiTranslation
							))
						);
					}
				}

				state.setIsPlacingOrder({
					...state.isPlacingOrder,
					isSubmittingWorkRequest: false,
					isUpdatingTranslations: false,
					workRequestId: workRequest._id
				});

				return workRequest;
			}
			catch(e) {
				const state = useWorkRequestQueueStore.getState();
				state.setIsPlacingOrder({
					isSubmittingWorkRequest: false,
					isUploadingFiles: false,
					isUpdatingTranslations: false,
					didFailWorkRequestOrder: true,
					didFailFileUpload: false,
					didFailTranslations: false
				});
				throw e;
			}
		})();
	},
	uploadFile: async(uploadRequest) => {
		let uploadMethod = Object.prototype.hasOwnProperty.call(uploadRequest, 'tagIndex')
			? UploadService.uploadTagFile
			: UploadService.uploadFile;

		const emitUpdate = (update) => {
			set((state) => ({
				fileUploadProgress: {
					...state.fileUploadProgress,
					[uploadRequest.id]: {
						name: uploadRequest.file.name,
						progress: update.progress / 100,
						error: update.error
					}
				},
				isPlacingOrder: {
					...state.isPlacingOrder,
					didFailFileUpload: _.reduce(state.fileUploadProgress, (result, entry) => {
						return result || !!entry.error;
					}, false)
				}
			}));
		};

		return uploadMethod(uploadRequest.file, uploadRequest, emitUpdate)
			.then((result) => {
				return {
					uploadRequest,
					result
				};
			})
			.catch((e) => {
				let err = _.isError(e) ? e : new Error(e);
				return {
					uploadRequest,
					result: err
				};
			});
	},
	uploadTranslations: async(translationId, translationFile) => {
		let uploadRequest = {
			translationId,
			file: translationFile
		};
		return UploadService.uploadTranslation(uploadRequest.file, uploadRequest)
			.then((result) => {
				return {
					uploadRequest,
					result
				};
			})
			.catch((e) => {
				let err = _.isError(e) ? e : new Error(e);
				return {
					uploadRequest,
					result: err
				};
			});
	},
	completePlacedOrder: (assetId) => set((state) => {
		if(!assetId) {
			console.error('completePlacedOrder called with invalid assetId:', assetId);
			return { isPlacingOrder: false };
		}

		let workRequestQueue = [...state.workRequestQueue];
		// Remove the completed asset from the queue
		workRequestQueue = workRequestQueue.filter((item) => item !== assetId);
		return {
			workRequestQueue,
			isPlacingOrder: false
		};
	})
}));

export default useWorkRequestQueueStore;